import React from "react"
import {
  compose,
  withProps,
  withPropsOnChange,
  mapProps,
  withState,
} from "recompact"
import { PrimaryButton, SecondaryButton } from "@infotech/uikit-button"
import withDynamicHOC from "src/hocs/withDynamicHOC"
import getCreateText from "src/helpers/text/getCreateText"
import getSaveText from "src/helpers/text/getSaveText"
import { ContentBox } from "src/components/Grid"
import Input from "src/components/Input"
import InputNumberHOC from "src/components/InputNumberHOC"
import DateTimePicker from "src/components/DateTimePicker"
import Select from "src/components/Selects/Select"
import { reduxForm } from "src/engine/modules/entity/plugins"
import entityApi from "src/hocs/entity"
import allResourceTypes from "src/hocs/allResourceTypes"
import allEventTypes from "src/hocs/allEventTypes"
import allPlans from "src/hocs/allPlans"
import allPlanTypes from "src/hocs/allPlanTypes"
import EventTypeSelect from "src/components/forms/EventTypeSelect"
import constraintModel from "src/models/constraint"
import { components } from "@infotech/uikit-select"
import { Box } from "@infotech/uikit-grid"
import {
  constraintTypeAPI,
  constraintAPI,
  argumentTypes,
  violationLevels,
} from "../helpers"
import ConstraintLayout from "../ConstraintLayout"
import ArgumentField from "./components/ArgumentField"
import ViolationField from "./components/ViolationField"
import styles from "./styles.css"

const ANY = `Любой`

export const ANY_OPTION = { name: ANY, id: null }

const SelectWithAny = props => {
  const Option = ({ data, ...props }) => {
    const isAny = Boolean(Number(data.value) === 0)
    return (
      <Box loose className={isAny && styles.AnyOptionSpace}>
        <components.Option data={data} {...props}>
          {data.label}
        </components.Option>
      </Box>
    )
  }

  const CustomPlaceholder = props => (
    <components.Placeholder
      {...props}
      getStyles={componentName => ({
        ...props.getStyles(componentName, props),
        color: props.getStyles(`option`, props).color,
      })}
    />
  )

  return (
    <div data-testid="plan-constraints-resource-type-select">
      <Select
        isClearable
        {...props}
        components={{ Placeholder: CustomPlaceholder, Option }}
      />
    </div>
  )
}

const ResourceTypeSelect = compose(
  allResourceTypes({ to: `resourceTypeList` }),
  withPropsOnChange(
    (props, nextProps) =>
      props.resourceTypeList &&
      nextProps.resourceTypeList &&
      props.resourceTypeList.data !== nextProps.resourceTypeList.data,
    ({ resourceTypeList: { data } }) => ({
      options: [ANY_OPTION, ...data].map(({ name, id }) => ({
        label: name,
        value: id ? String(id) : id,
      })),
      placeholder: ANY,
    }),
  ),
)(SelectWithAny)

const PlanSelect = compose(
  allPlans({
    to: `planList`,
    query: { limit: -1, orderBy: `name asc`, offset: 0 },
  }),
  withPropsOnChange(
    (props, nextProps) =>
      props.planList &&
      nextProps.planList &&
      props.planList.data !== nextProps.planList.data,
    ({ planList: { data } }) => ({
      options: [ANY_OPTION, ...data].map(({ name, id }) => ({
        label: name,
        value: id ? String(id) : id,
      })),
      placeholder: ANY,
    }),
  ),
)(SelectWithAny)

const PlanTypeSelect = compose(
  allPlanTypes({
    to: `planTypesList`,
    query: { limit: -1, orderBy: `name asc`, offset: 0 },
  }),
  withPropsOnChange(
    (props, nextProps) =>
      props.planTypesList &&
      nextProps.planTypesList &&
      props.planTypesList.data !== nextProps.planTypesList.data,
    ({ planTypesList: { data } }) => ({
      options: [ANY_OPTION, ...data].map(({ name, id }) => ({
        label: name,
        value: id ? String(id) : id,
      })),
      placeholder: ANY,
    }),
  ),
)(SelectWithAny)

const EventTypeSelectWrapper = compose(
  allEventTypes({ to: `eventTypeList` }),
  withPropsOnChange(
    (props, nextProps) =>
      props.eventTypeList &&
      nextProps.eventTypeList &&
      props.eventTypeList.data !== nextProps.eventTypeList.data,
    ({ eventTypeList: { data } }) => ({
      options: [ANY_OPTION, ...data].map(({ name, id, color }) => ({
        label: name,
        value: id ? String(id) : id,
        color: color || false,
      })),
      placeholder: ANY,
      haveAny: true,
    }),
  ),
)(props => (
  <div data-testid="plan-constraints-event-type-select">
    <EventTypeSelect {...props} />
  </div>
))

const getComponentForArgument = argument => {
  const {
    RESOURCE_TYPE,
    EVENT_TYPE,
    PLAN,
    PLAN_TYPE,
    SELECT,
    TIMESTAMP,
    INT,
    LONG,
  } = argumentTypes

  const argumentType = constraintTypeAPI.getArgumentType(argument)

  switch (argumentType) {
    case SELECT:
      return props => (
        <Select
          clearable={!constraintTypeAPI.isArgumentRequired(argument)}
          options={constraintTypeAPI.getArgumentOptions(argument)}
          {...props}
        />
      )
    case INT:
      return props => (
        <div data-testid="constraint-field-integer">
          <InputNumberHOC {...props} />
        </div>
      )
    case LONG:
      return props => (
        <div data-testid="constraint-field-long">
          <InputNumberHOC {...props} />
        </div>
      )
    case TIMESTAMP:
      return DateTimePicker
    case RESOURCE_TYPE:
      return ResourceTypeSelect
    case EVENT_TYPE:
      return EventTypeSelectWrapper
    case PLAN:
      return PlanSelect
    case PLAN_TYPE:
      return PlanTypeSelect
    default: {
      return Input
    }
  }
}

const mapArgumentsToFields = (args, pathPrefix) =>
  args.map(argument => (
    <ArgumentField
      key={constraintTypeAPI.getArgumentName(argument)}
      htmlId={constraintTypeAPI.getArgumentName(argument)}
      name={`${pathPrefix}${constraintTypeAPI.getArgumentName(argument)}`}
      label={constraintTypeAPI.getArgumentLabel(argument)}
      elementType={constraintTypeAPI.getArgumentElementType(argument)}
      InputComponent={getComponentForArgument(argument)}
    />
  ))

const getLabelForViolationLevel = violationLevel => {
  const { ERROR, WARNING } = violationLevels

  switch (violationLevel) {
    case ERROR:
      return `Ошибку`
    case WARNING:
      return `Предупреждение`
    default:
      return violationLevel
  }
}

const argsArrayKey = `argumentValues`

// I used dynamic HOC, because I need to create a model based on initial planId prop
export default withDynamicHOC(
  () => false,
  props =>
    compose(
      mapProps(props => ({ outerProps: props })),

      withState(`createdId`, `setCreatedId`, null),

      // id prop is used by redux form module
      withProps(({ outerProps: { constraint }, createdId }) => ({
        id: createdId || (constraint ? constraintAPI.getId(constraint) : null),
      })),
      entityApi({
        to: `constraintEntity`,
        model: constraintModel(props.planId, props.basicUrl),
        onCreate: ({ setCreatedId }) => ({ createdId }) =>
          setCreatedId(createdId),
        plugins: reduxForm(
          compose(
            withProps(props => ({
              initialValues: {
                violationLevel: violationLevels.ERROR,
                constraintTypeId: constraintTypeAPI.getId(
                  props.outerProps.constraintType,
                ),
                ...props.initialValues,
              },
            })),
            withProps({
              form: `constraintForm`,
              enableReinitialise: true,
              onSubmit: (
                data,
                dispatch,
                {
                  constraintEntity: {
                    api: { save },
                  },
                },
              ) => save(data),
            }),
          ),
        ),
      }),

      // Do it for consistency, so the form related props will be separated from others, not mixed together
      mapProps(({ outerProps, ...restProps }) => ({
        ...outerProps,
        formProps: { ...restProps },
      })),
      // close form after success submit
      withPropsOnChange(
        (props, nextProps) =>
          !!props.formProps &&
          !props.formProps.invalid &&
          !props.formProps.submitSucceeded &&
          nextProps.formProps.submitSucceeded,
        ({ onCancel }) => onCancel(),
      ),
    ),
)(props => {
  const {
    constraint,
    constraintType,
    onCancel,
    formProps: {
      constraintEntity: { pending },
      createdId,
      handleSubmit,
      dirty,
      invalid,
    },
  } = props

  const isEdit = constraint || createdId

  const isSubmitButtonDisabled = isEdit && (pending || !dirty || invalid)

  const submitButtonText = isEdit
    ? getSaveText(pending, true)
    : getCreateText(pending, true)

  return (
    <div className={styles.ConstraintForm}>
      <ConstraintLayout
        bodyContent={
          <ContentBox>
            {mapArgumentsToFields(
              constraintTypeAPI.getForm(constraintType),
              `${argsArrayKey}.`,
            )}
            <ViolationField
              name="violationLevel"
              errorLabel={getLabelForViolationLevel(violationLevels.ERROR)}
              errorValue={violationLevels.ERROR}
              warningLabel={getLabelForViolationLevel(violationLevels.WARNING)}
              warningValue={violationLevels.WARNING}
            />
          </ContentBox>
        }
        buttons={
          <>
            <PrimaryButton
              type="submit"
              style={{ marginRight: 8 }}
              onClick={handleSubmit}
              disabled={isSubmitButtonDisabled}
            >
              {submitButtonText}
            </PrimaryButton>
            <SecondaryButton onClick={onCancel} type="button">
              Отмена
            </SecondaryButton>
          </>
        }
      />
    </div>
  )
})
